import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "utilities/i18n";
import { nameIsUnassigned } from "utilities/stringUtils";
import { getMTRAppDeviceId } from "utilities/roomUtils";
import {
  NavLink,
  Redirect,
  Route,
  Switch,
  useHistory,
  useParams
} from "polyrouter";
import { RoomQuery, useRoomQuery } from "generated/graphql";
import { Layout } from "views/Main/Layouts";
import { DeviceTable } from "routes/Manage/Sites/View/DevicesTable";
import { CategoryCard } from "routes/Manage/Sites/View/SiteOverview";
import { Loader, LoadingIndicator } from "elements";
import { GeneralRoomSettings } from "../Settings/GeneralRoomSettings";
import { generatePath, useRouteMatch } from "react-router";
import "./RoomDetails.scss";
import { RoomAnalysis } from "./RoomAnalysis";
import { SelectedTenantContext } from "polyrouter/SelectedTenantProvider";
import { MTRRoomDetails } from "./MTRRoomDetails";

export const RoomSettingsRouter: React.FC<{ id: string }> = ({ id }) => {
  return (
    <Switch>
      <Route nest path="/general">
        <GeneralRoomSettings id={id} />
      </Route>
      <Route>
        <Redirect nest to="/general" />
      </Route>
    </Switch>
  );
};

export const RoomDetailsWrapper: React.FC<{ id: string }> = ({ id }) => {
  const { Content } = Layout;
  const { tenant } = useContext(SelectedTenantContext);
  const tenantId = tenant?.id ?? "";
  const history = useHistory();
  const [dispTid, setDispTid] = useState<string | undefined>(undefined);

  const { data: roomData, fetching } = useRoomQuery({
    variables: {
      id: id || ""
    }
  });

  const room = roomData?.room!;
  const isMTR = !!getMTRAppDeviceId(room);

  useEffect(() => {
    if (!dispTid) {
      setDispTid(tenantId);
      return;
    }
    if (tenantId !== dispTid) {
      history.push("/manage/rooms");
    }
  }, [tenantId, dispTid, setDispTid, history]);

  if (!room || fetching) {
    return (
      <Content>
        <LoadingIndicator />
      </Content>
    );
  }

  return (
    <Content className="room-details">
      <Loader loading={fetching}>
        {!isMTR && <RoomDetails room={room} />}
        {isMTR && <MTRRoomDetails room={room} />}
      </Loader>
    </Content>
  );
};

export const RoomDetails: React.FC<{
  room: NonNullable<RoomQuery["room"]>;
}> = ({ room }) => {
  const { Content } = Layout;
  const { t } = useTranslation();

  return (
    <>
      <Content.Title>
        {t("OVERVIEW")} <p>{`| ${room.name || ""}`}</p>
      </Content.Title>
      <div className="grouping d-flex clickable">
        <div data-testid="device-count">
          <CategoryCard
            title={t("DEVICES")}
            count={room.devices?.length || 0}
            selected={true}
          />
        </div>
      </div>
      <DeviceTable roomId={room.id} />
      {nameIsUnassigned(room.name) ? (
        <Redirect nest to="instructions" push={true} />
      ) : null}
    </>
  );
};

export const RoomDetailsRouter: React.FC = () => {
  const { t } = useTranslation();
  const { roomId } = useParams<{ roomId: string }>();
  const { Sidebar } = Layout;
  const { url } = useRouteMatch();

  let disableSettings = false;

  const { data, fetching } = useRoomQuery({
    variables: {
      id: roomId || ""
    }
  });

  if (nameIsUnassigned(data?.room?.name) || fetching) {
    disableSettings = true;
  }

  const backUrl = url
    .replace(`/${roomId}`, "")
    .replace("/rooms/details", "/rooms")
    .replace("/insight-room", "");

  return (
    <div className="site-details-content">
      <Layout
        back={{
          to: generatePath(backUrl),
          persist: ["InsightsFilterState"],
          title: t(url.includes("insights") ? "INSIGHT_DETAILS" : "ROOMS"),
          iconDesc: t("RETURN_TO_ROOMS_LIST")
        }}
      >
        <>
          <Sidebar>
            <ul className="menu router">
              <li>
                <NavLink nest exact to="">
                  {t("OVERVIEW")}
                </NavLink>
              </li>
              <li>
                {!disableSettings ? (
                  <>
                    <NavLink nest exact={false} to="/settings">
                      {t("SETTINGS")}
                    </NavLink>
                    <Route nest path="/settings">
                      <ul className="menu router sub">
                        <li>
                          <NavLink nest exact to="/general">
                            {t("GENERAL")}
                          </NavLink>
                        </li>
                      </ul>
                    </Route>
                  </>
                ) : (
                  <span className="nav-link-disabled">{t("SETTINGS")}</span>
                )}
              </li>
              {!disableSettings && (
                <li>
                  <NavLink nest to="/analysis">
                    {t("ANALYSIS")}
                  </NavLink>
                </li>
              )}
            </ul>
          </Sidebar>
          <Switch>
            <Route nest path="/settings">
              <RoomSettingsRouter id={roomId} />
            </Route>
            <Route nest path="/analysis">
              <RoomAnalysis id={roomId} name={data?.room?.name} />
            </Route>
            <Route nest path="/">
              <RoomDetailsWrapper id={roomId} />
            </Route>
          </Switch>
        </>
      </Layout>
    </div>
  );
};
